import { useRef, useEffect, useMemo } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { __ } from '@wordpress/i18n';
import {
	InnerBlocks,
	MediaUploadCheck,
	MediaPlaceholder,
	RichText,
} from '@wordpress/block-editor';
import PanelSettings from './advance/inspector-control';
import Advance from './advance';
import DuotoneFilters from '@vayu-blocks/duotone/DuotoneFilters.js';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import {
	OverlayStyle,
	CaptionStyle,
	getImageStyle,
	getImagePositionStyle,
	getInlineStyles,
} from './advance/inline-style';
import ImageToolbar from './advance/block-control';
import clsx from 'clsx';
import { VideoHtml, MouseFollower } from '@vayu-blocks';
import { VayuBlocksUniqueId } from '@vayu-blocks/global';
import useTiltEffect  from './advance/useTiltEffect'; 
import SimpleParallax from 'simple-parallax-js/vanilla';

const edit = ({ attributes, setAttributes, clientId, isSelected }) => {
	const view = VayuBlocksDevice();
	const imageWrapperRef = useRef(null);
	const overlayWrapperRef = useRef(null);

	VayuBlocksUniqueId({ setAttributes, clientId });

	const parentBlockData = useSelect((select) => {
		const { getBlockParents, getBlock } = select('core/block-editor');
		let currentId = clientId;
		while (true) {
			const parentIds = getBlockParents(currentId, false);
			if (!parentIds?.length) break;
			const block = getBlock(parentIds[0]);
			if (!block) break;
			if (block.name?.includes('vayu-blocks/advance-slider')) {
				return {
					name: block.name,
					attributes: block.attributes || {},
				};
			}
			currentId = parentIds[0];
		}
		return { name: null, attributes: {} };
	}, [clientId]);

	useEffect(() => {
		if (parentBlockData.name) {
			setAttributes({
				parentBlock: parentBlockData.name,
				advanceglobaldropshadow: parentBlockData.attributes.advanceglobaldropshadow,
				sliderframeData: parentBlockData.attributes.frameData,
			});
		}
	}, [parentBlockData]);

	const image_template = useMemo(() => [
		[
			'vayu-blocks/advance-container',
			{
				contFlex: {
					direction: { Desktop: 'column', Tablet: 'column', Mobile: 'column' },
					justifyContent: { Desktop: 'center', Tablet: 'center', Mobile: 'center' },
					alignItems: { Desktop: 'center', Tablet: 'center', Mobile: 'center' },
					rwGap: { Desktop: '0px', Tablet: '0px', Mobile: '0px' },
				},
			},
			[
				['vayu-blocks/advance-heading', { className: 'vayu-blocks-heading-innerblock', align: { Desktop: 'center' } }],
				['core/paragraph', { align: 'center', placeholder: 'write here please.....', className: 'vayu-blocks-para-innerblock' }],
				['vayu-blocks/advance-button', { alignment: { Desktop: 'center', Tablet: 'center', Mobile: 'center' } }],
			],
		],
	], []);

	const currentTemplate = useMemo(() => {
		return attributes.overlayshow && attributes.parentBlock === 'vayu-blocks/image-hotspot' ? [] : image_template;
	}, [attributes.overlayshow, attributes.parentBlock, image_template]);

	const handleimageurl = ({ url, width }) => setAttributes({ image: url, defaultImageWidth: width });
	const handleimage = ({ url, width }) => setAttributes({ image: url, defaultImageWidth: width });

    // Inside the component body
    useTiltEffect(imageWrapperRef, attributes.animationData.effect.animationValue === 'vayu_block_styling-effect7', {
        multiplierX: 4,
        multiplierY: 4,
        perspective: 500,
    });

    useTiltEffect(overlayWrapperRef, attributes.animationData.effect.animationValue === 'vayu_block_styling-effect7', {
        multiplierX: 6,
        multiplierY: 6,
        perspective: 500,
    });

	const styles = useMemo(() => ({
		image: getImageStyle(attributes, view),
		position: getImagePositionStyle(attributes, view),
		overlay: OverlayStyle(attributes, view),
		caption: CaptionStyle(attributes, view),
		inline: getInlineStyles(attributes, view),
	}), [attributes, view]);

	const getOverlayClass = () => {
		const { imageAnimation = {} } = attributes.animationData || {};
		if (imageAnimation.hover === 'with-hvr') return `${imageAnimation.animationValue}hvr`;
		if (imageAnimation.hover === 'one-time') return `${imageAnimation.animationValue}onetime`;
		return imageAnimation.animationValue ?? '';
	};

	const wrapper = useMemo(() => clsx(
		attributes.contentani && 'vb-start-cont-ani',
		attributes.animationData?.effect?.effectHover && 'vayu-blocks-image-hover'
	), [attributes]);

	const container = useMemo(() => clsx(
		'vb-image-wrapper',
		attributes.animationData?.effect?.animationValue !== 'none' && attributes.animationData?.effect?.animationValue
	), [attributes]);

	const imageContainer = useMemo(() => clsx(
		'vb-image-container',
		attributes.animationData?.hovereffect?.value,
		getOverlayClass()
	), [attributes]);

	const imageTagClass = useMemo(() => clsx(
		'vb-image-image-tag',
		attributes.animationData?.mask?.maskshape !== 'none' && 'maskshapeimage'
	), [attributes]);

	const overlayWrapperClass = useMemo(() => clsx(
		'vb-image-overlay-wrapper',
		attributes.animationData?.hovereffect?.value,
		attributes.animationData?.mask?.maskshape !== 'none' && 'maskshapeimage',
		getOverlayClass(),
		attributes.overlayshow && attributes.overlayHoverMode === 'hover' && 'vb-overlay-on-hover'
	), [attributes]);


	const imgRef = useRef(null);
	//parallax effect
	useEffect(() => {
		if (
			attributes?.parallax?.switch &&
			imgRef.current &&
			typeof window !== 'undefined'
		) {
			// Default container is null
			let customContainer = null;

			// Check for content area container (normal editor)
			const editorContentContainer = document.querySelector('.interface-navigable-region.interface-interface-skeleton__content');

			// Check if iframe exists and get its HTML root
			const iframe = document.querySelector('iframe[name="editor-canvas"]');
			if (iframe?.contentWindow?.document) {
				const iframeHtml = iframe.contentWindow.document.querySelector('.block-editor-iframe__html');
				if (iframeHtml) {
					customContainer = iframeHtml;
				}
			}

			// Use editor content container if no iframe found
			if (!customContainer && editorContentContainer) {
				customContainer = editorContentContainer;
			}

			const options = {
				orientation: attributes.parallax.orientation || 'up',
				delay: parseFloat(attributes.parallax.delay || 0),
				scale: parseFloat(attributes.parallax.scale || 1.2),
				transition: attributes.parallax.transition || 'cubic-bezier(0,0,0,1)',
				overflow: attributes.parallax.overflow ?? true,
				maxTransition: parseFloat(attributes.parallax.maxTransition || 60),
				customContainer,
				customWrapper: '.vb-image-wrapper',
			};

			const parallax = new SimpleParallax(imgRef.current, options);

			return () => {
				parallax.destroy();
			};
		}
	}, [
		attributes.parallax?.switch,
		attributes.parallax?.orientation,
		attributes.parallax?.delay,
		attributes.parallax?.scale,
		attributes.parallax?.transition,
		attributes.parallax?.overflow,
		attributes.parallax?.maxTransition,
	]);

	return (
		<>
			{isSelected && <ImageToolbar attributes={attributes} setAttributes={setAttributes} />}

			<PanelSettings attributes={attributes} setAttributes={setAttributes} isSelected={isSelected} clientId={clientId} />
			<Advance attributes={attributes} clientId={clientId}>
				<VideoHtml bg={attributes.advBackground} />
				<div ref={imageWrapperRef} className="vayu-blocks-image-main-container" style={styles.inline}>
					<div style={{ transform: `rotate(${attributes.rotation}deg)`, ...styles.position }} className={wrapper}>
						{Array.isArray(attributes.duotone) && attributes.duotone.length > 1 && (
							<div className="vayu-blocks-dustone-svg">
								<DuotoneFilters duotone={attributes.duotone} uniqueId={attributes.uniqueId} />
							</div>
						)}

						{attributes.image && (
							<div className={container}>
								<div className={imageContainer}>
									
										<img ref={imgRef}  style={styles.image} src={attributes.image} alt={attributes.imagealttext} className={imageTagClass} />
							
								</div>
								{(attributes.overlayshow || attributes.frameshow) && (
									<div ref={overlayWrapperRef} className={overlayWrapperClass} style={styles.overlay}>
										{attributes.overlayshow && <InnerBlocks template={currentTemplate} />}
									</div>
								)}
							</div>
						)}
					</div>

					{!attributes.image && attributes.typeimage === 'image' && (
						<MediaUploadCheck>
							<MediaPlaceholder
								icon="format-image"
								labels={{ title: __('Background Image', 'vayu-blocks'), name: __('an image', 'vayu-blocks') }}
								onSelect={handleimageurl}
								onSelectURL={handleimage}
								accept="image/*"
								allowedTypes={['image']}
							/>
						</MediaUploadCheck>
					)}

					{attributes.caption && (
						<div className="vb-image-caption">
							<RichText
								tagName="p"
								value={attributes.captiontext}
								onChange={(val) => setAttributes({ captiontext: val })}
								placeholder={__('Add caption...', 'vayu-blocks')}
								style={styles.caption}
							/>
						</div>
					)}
				</div>

				<MouseFollower value={attributes.advFollower} clientId={clientId} />
			</Advance>
		</>
	);
};

export default edit;